/**
 * Turning a triangle index buffer into a line index buffer.
 *
 * Drawing `gl.LINES` over the triangle indices directly would draw every
 * interior edge twice - once for each triangle that owns it - which doubles
 * the vertex work and, more visibly, doubles the coverage on every shared edge
 * so a dense mesh reads as a solid dark surface rather than as a wireframe.
 *
 * So edges are deduplicated. The key is the ordered vertex pair packed into
 * one number, because a `Set` of strings over the three million edges of a
 * decimated Poisson mesh spends most of its time in the string allocator.
 *
 * **The cap is the point of the rest of this file.** A wireframe of a
 * half-million-face mesh is one and a half million lines, which is not a
 * picture of the topology - it is a grey rectangle - and it costs a large
 * buffer upload to produce. Above the cap the wireframe is refused and the
 * viewer says why, rather than freezing the tab to draw something unreadable.
 */

/** Above this many triangles a wireframe is not built. */
export const WIREFRAME_FACE_LIMIT = 400_000;

export interface WireframeResult {
  indices: Uint32Array | null;
  /** Set when no wireframe was built, in the customer's words. */
  refusal: string | null;
  edgeCount: number;
}

export function buildWireframe(
  indices: Uint32Array,
  vertexCount: number,
): WireframeResult {
  const faceCount = Math.floor(indices.length / 3);
  if (faceCount === 0) {
    return { indices: null, refusal: null, edgeCount: 0 };
  }
  if (faceCount > WIREFRAME_FACE_LIMIT) {
    return {
      indices: null,
      refusal:
        "Bu model tel kafes için fazla yoğun; yüzey görünümü tam çözünürlükte kalıyor.",
      edgeCount: 0,
    };
  }

  // A pair (a, b) with a < b packs into a * vertexCount + b, which is unique
  // as long as the product stays exact. Above 2^53 it would not, so the guard
  // above is doing double duty and this assertion states the second reason.
  const seen = new Set<number>();
  const out: number[] = [];
  const consider = (a: number, b: number) => {
    const low = a < b ? a : b;
    const high = a < b ? b : a;
    const key = low * vertexCount + high;
    if (seen.has(key)) return;
    seen.add(key);
    out.push(low, high);
  };

  for (let face = 0; face < faceCount; face += 1) {
    const a = indices[face * 3] as number;
    const b = indices[face * 3 + 1] as number;
    const c = indices[face * 3 + 2] as number;
    consider(a, b);
    consider(b, c);
    consider(c, a);
  }

  return {
    indices: new Uint32Array(out),
    refusal: null,
    edgeCount: out.length / 2,
  };
}
